import React from "react";
import useConfig from "@hooks/useConfig";
import { Mail, MapPin, Phone } from "lucide-react";

// Shared Styles
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Label,
  Value,
} from "../../styles";

const CustomerInfo = ({ order, lastOrder }: any) => {
  const info = order?.customer || {};
  const shipping = order?.shippingInfos || {};
  const { bucketUrl } = useConfig();

  // Helper to render rows
  const InfoRow = ({
    icon: Icon,
    label,
    value,
  }: {
    icon: any;
    label: string;
    value: string;
  }) => (
    <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
      <div
        style={{
          minWidth: "32px",
          height: "32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f3f4f6",
          borderRadius: "8px",
          color: "#6b7280",
        }}
      >
        <Icon size={16} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        <Label>{label}</Label>
        <Value>{value}</Value>
      </div>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Customer Details</CardTitle>
      </CardHeader>

      <CardContent>
        {/* Avatar & Name */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            paddingBottom: "20px",
            marginBottom: "20px",
            borderBottom: "1px dashed #e5e7eb",
          }}
        >
          <img
            src={
              info.avatar
                ? `${bucketUrl}/${info.avatar}`
                : "https://placehold.co/100?text=User"
            }
            alt="Customer"
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "50%",
              objectFit: "cover",
              backgroundColor: "#f3f4f6",
              border: "2px solid #fff",
              boxShadow: "0 0 0 1px #e5e7eb",
            }}
            onError={(e) => {
              e.currentTarget.src = "https://placehold.co/100?text=User";
            }}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span
              style={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#111827",
              }}
            >
              {info.name || "Guest User"}
            </span>
            <span style={{ fontSize: "13px", color: "#6b7280" }}>
              Order #{order?.id}
            </span>
          </div>
        </div>

        {/* Contact Info */}
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <InfoRow
            icon={Phone}
            label="Phone Number"
            value={info.phoneNo || "N/A"}
          />
          <InfoRow
            icon={Mail}
            label="Email Address"
            value={info.email || "N/A"}
          />
          <InfoRow
            icon={MapPin}
            label="Shipping Address"
            value={
              [
                shipping.addressOne,
                shipping.addressTwo,
                shipping.city,
                shipping.state,
                shipping.zipcode,
              ]
                .filter(Boolean)
                .join(", ") || "N/A"
            }
          />
        </div>

        {/* Last Order Info */}
        {lastOrder && (
          <div
            style={{
              marginTop: "24px",
              paddingTop: "20px",
              borderTop: "1px dashed #e5e7eb",
            }}
          >
            <div
              style={{
                marginBottom: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#111827",
                }}
              >
                Last Order
              </div>
              <a
                href={`/admin/orders/details/${lastOrder.id}`}
                style={{
                  fontSize: "12px",
                  color: "#2563eb",
                  textDecoration: "none",
                  fontWeight: 500,
                }}
              >
                View Details &rarr;
              </a>
            </div>

            <div
              style={{
                background: "#f9fafb",
                borderRadius: "12px",
                padding: "16px",
                border: "1px solid #f3f4f6",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "12px",
                }}
              >
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span
                    style={{
                      fontSize: "16px",
                      fontWeight: 700,
                      color: "#111827",
                    }}
                  >
                    #{lastOrder.id}
                  </span>
                  <span style={{ fontSize: "12px", color: "#6b7280" }}>
                    {new Date(lastOrder.createdAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                </div>
                <div style={{ textAlign: "right" }}>
                  <span
                    style={{
                      fontSize: "16px",
                      fontWeight: 700,
                      color: "#059669",
                    }}
                  >
                    NPR {lastOrder.totalAmount?.toLocaleString()}
                  </span>
                  <div
                    style={{
                      marginTop: "4px",
                      display: "inline-block",
                      padding: "2px 8px",
                      borderRadius: "12px",
                      background: "#d1fae5",
                      color: "#065f46",
                      fontSize: "10px",
                      fontWeight: 600,
                      textTransform: "uppercase",
                    }}
                  >
                    {lastOrder.status}
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  marginTop: "12px",
                  paddingTop: "12px",
                  borderTop: "1px dashed #e5e7eb",
                  fontSize: "12px",
                  color: "#6b7280",
                }}
              >
                <span>📦 {lastOrder.items?.length || 0} Items</span> •
                <span>💳 {lastOrder.paymentStatus || "Unpaid"}</span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CustomerInfo;
